import React, { Suspense, useEffect } from 'react';
import {
  useTheme,
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from '@mui/material/styles';
import { Typography } from '@mui/material';
import { ThemeProvider as StyledThemeProvider } from 'theme/styled';
import { useParams } from 'react-router';
import { isApps, AppColor } from 'core/types/app';
import MainNavigationHeader from 'core/components/main-navigation';
import AppNavigation from 'core/components/app/navigation';
import availableApps from 'apps/available';
import { useAppAccess, roleGivesAccess } from 'core/data/app-access';
import { Container } from 'core/components/layout';

declare module '@mui/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

interface AppThemeProviderProps {
  color: AppColor;
  children: React.ReactNode;
}

function AppThemeProvider({
  color,
  children,
}: AppThemeProviderProps): JSX.Element {
  const theme = useTheme();

  const newTheme = {
    ...theme,
    palette: {
      ...theme.palette,
      primary: color,
    },
  };

  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name=theme-color]');
    metaThemeColor?.setAttribute('content', color.light);
  }, [color]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={newTheme}>
        <StyledThemeProvider theme={newTheme}>{children}</StyledThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

function AppNotFound(): JSX.Element {
  return (
    <>
      <MainNavigationHeader active={availableApps.manage} />
      <Container>
        <Typography>App not found.</Typography>
      </Container>
    </>
  );
}

export default function AppPage(): JSX.Element {
  const { appId } = useParams<{ appId: string }>();
  const access = useAppAccess();

  if (!isApps(appId)) {
    return <AppNotFound />;
  }

  if (!access[appId].enabled || !roleGivesAccess(access[appId].role)) {
    return <AppNotFound />;
  }

  const app = availableApps[appId];
  const LazyApp = app.app;

  return (
    <AppThemeProvider color={app.color}>
      <MainNavigationHeader active={app} />
      <AppNavigation navigation={app.navigation} appId={appId} />
      <Suspense fallback={<p />}>
        <LazyApp />
      </Suspense>
    </AppThemeProvider>
  );
}
